var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { toJSON } from "@utils";
/**
 * Import of an embedded dashboard file into this site: the user group and breakpoint mapping.
 * The rules are the dashboard import's (UXP Dashboard.py: _remap_layout, _remap_grids, _scale_grid),
 * so an embedded dashboard and a dashboard come out of an import the same way.
 */
/** The column count assumed when a breakpoint's columns are not known, as the dashboard import. */
const DEFAULT_COLUMNS = 30;
/** The export's record of the site it came from: its breakpoints and their columns. */
export const SOURCE_KEY = '__source__';
/** The configuration without the export's source record: what is stored and rendered. */
export function withoutSource(config) {
    if (!config || typeof config !== 'object' || !(SOURCE_KEY in config))
        return config;
    const _a = config, _b = SOURCE_KEY, _source = _a[_b], rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
    return rest;
}
const isMap = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
/**
 * Reads an export: null when the text is not an embedded dashboard file. A file has a default layout
 * (widgets, layouts), user group layouts, or both: a dashboard set up only per group has no default.
 */
export function readEmbeddedDashboardFile(text) {
    const json = toJSON(text, null); // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!isMap(json) || json.dashboard)
        return null;
    if (!('widgets' in json) && !('layouts' in json) && !('userGroupLayouts' in json))
        return null;
    if ('widgets' in json && !Array.isArray(json.widgets))
        return null;
    if ('layouts' in json && !isMap(json.layouts))
        return null;
    if ('userGroupLayouts' in json && !isMap(json.userGroupLayouts))
        return null;
    const source = json[SOURCE_KEY];
    return {
        config: withoutSource(json),
        source: source && Array.isArray(source.breakpoints) ? source : null,
    };
}
/** A dashboard export (not an embedded one): it has a top-level `dashboard`. */
export function isDashboardExport(text) {
    const json = toJSON(text, null); // eslint-disable-line @typescript-eslint/no-explicit-any
    return !!json && typeof json === 'object' && !!json.dashboard;
}
/** Scales a grid from one column count to another. A cell is square, so rows scale with the columns. */
export function scaleGrid(items, fromColumns, toColumns) {
    if (!Array.isArray(items))
        return [];
    const factor = toColumns / fromColumns;
    return items.map((item) => {
        var _a, _b, _c, _d;
        if (!item || typeof item !== 'object')
            return item;
        const copy = Object.assign({}, item);
        if (fromColumns === toColumns || !('x' in copy) || !('w' in copy))
            return copy;
        const w = Math.min(Math.max(Math.round(Number((_a = copy.w) !== null && _a !== void 0 ? _a : 1) * factor), 1), toColumns);
        let x = Math.round(Number((_b = copy.x) !== null && _b !== void 0 ? _b : 0) * factor);
        if (x + w > toColumns)
            x = toColumns - w;
        if (x < 0)
            x = 0;
        copy.x = x;
        copy.w = w;
        for (const limit of ['minW', 'maxW']) {
            if (copy[limit] != null)
                copy[limit] = Math.min(Math.max(Math.round(Number(copy[limit]) * factor), 1), toColumns);
        }
        if ('h' in copy)
            copy.h = Math.max(Math.round(Number((_c = copy.h) !== null && _c !== void 0 ? _c : 1) * factor), 1);
        if ('y' in copy)
            copy.y = Math.round(Number((_d = copy.y) !== null && _d !== void 0 ? _d : 0) * factor);
        for (const limit of ['minH', 'maxH']) {
            if (copy[limit] != null)
                copy[limit] = Math.max(Math.round(Number(copy[limit]) * factor), 1);
        }
        return copy;
    });
}
/** The breakpoints a configuration has grids for, in the default layout and every user group layout. */
export function fileBreakpoints(config) {
    const keys = [];
    const add = (slot) => Object.keys((slot === null || slot === void 0 ? void 0 : slot.layouts) || {}).forEach((k) => !keys.includes(k) && keys.push(k));
    add(config);
    Object.values(config.userGroupLayouts || {}).forEach((slot) => add(slot));
    return keys;
}
/** The column count of each file breakpoint. Without a source record: this site's columns, so nothing scales. */
export function sourceColumns(source, keys, site) {
    const out = {};
    keys.forEach((key) => {
        var _a, _b, _c;
        const fromFile = (_a = source === null || source === void 0 ? void 0 : source.breakpoints.find((b) => b.key === key)) === null || _a === void 0 ? void 0 : _a.columns;
        out[key] = Number((_c = fromFile !== null && fromFile !== void 0 ? fromFile : (_b = site[key]) === null || _b === void 0 ? void 0 : _b.columns) !== null && _c !== void 0 ? _c : DEFAULT_COLUMNS);
    });
    return out;
}
/** The proposed source for each site breakpoint: the same name, or the file's lg (or first) breakpoint. */
export function proposeBreakpointMap(config, site) {
    const keys = fileBreakpoints(config);
    const grids = (config.layouts || {});
    const withItems = (key) => Array.isArray(grids[key]) && grids[key].length > 0;
    const fallback = withItems('lg') ? 'lg' : keys.find(withItems) || keys[0] || '';
    const out = {};
    Object.keys(site).forEach((target) => {
        out[target] = keys.includes(target) ? target : fallback;
    });
    return out;
}
/** Rebuilds one slot's grids for this site: each site breakpoint takes its mapped file grid, scaled. */
function remapSlot(slot, map, columns, site) {
    var _a;
    const grids = slot.layouts || {};
    const fileKeys = Object.keys(grids);
    if (fileKeys.length === 0) {
        // No grids: widgets keep their place in widget.layout, made on the source lg columns
        const placed = (slot.widgets || []).map((w) => w === null || w === void 0 ? void 0 : w.layout).filter((p) => p && 'x' in p && 'w' in p);
        if (placed.length === 0)
            return slot;
        const from = (_a = columns.lg) !== null && _a !== void 0 ? _a : DEFAULT_COLUMNS;
        const layouts = {};
        Object.entries(site).forEach(([key, bp]) => (layouts[key] = scaleGrid(placed, from, bp.columns)));
        return Object.assign(Object.assign({}, slot), { layouts });
    }
    const withItems = (key) => Array.isArray(grids[key]) && grids[key].length > 0;
    const fallback = withItems('lg') ? 'lg' : fileKeys.find(withItems) || '';
    const layouts = {};
    Object.entries(site).forEach(([target, bp]) => {
        var _a;
        let source = (map[target] || '').trim();
        if (!fileKeys.includes(source))
            source = fallback;
        layouts[target] = source ? scaleGrid(grids[source], (_a = columns[source]) !== null && _a !== void 0 ? _a : DEFAULT_COLUMNS, bp.columns) : [];
    });
    return Object.assign(Object.assign({}, slot), { layouts });
}
/**
 * The configuration for this site.
 * groupMap: file group name -> group name here, '' to leave the group's layout out.
 * breakpointMap: site breakpoint -> file breakpoint.
 */
export function importConfiguration(file, groupMap, breakpointMap, site) {
    const columns = sourceColumns(file.source, fileBreakpoints(file.config), site);
    const base = remapSlot(file.config, breakpointMap, columns, site);
    const groups = file.config.userGroupLayouts;
    if (!groups)
        return base;
    const userGroupLayouts = {}; // eslint-disable-line @typescript-eslint/no-explicit-any
    Object.entries(groups).forEach(([name, slot]) => {
        var _a;
        const target = (_a = groupMap[name]) !== null && _a !== void 0 ? _a : name;
        if (!target)
            return; // left out
        userGroupLayouts[target] = remapSlot(slot, breakpointMap, columns, site);
    });
    return Object.assign(Object.assign({}, base), { userGroupLayouts });
}
/** Site breakpoints whose column count differs from the file breakpoint mapped to them. */
export function scaledBreakpoints(file, breakpointMap, site) {
    if (!file.source)
        return [];
    const columns = sourceColumns(file.source, fileBreakpoints(file.config), site);
    return Object.entries(site)
        .filter(([target, bp]) => {
        const source = breakpointMap[target];
        return !!source && columns[source] !== undefined && columns[source] !== bp.columns;
    })
        .map(([target]) => target);
}
/** Whether the file's breakpoints differ from this site's: other names, or (with a source) other columns. */
export function breakpointsDiffer(file, site) {
    const keys = fileBreakpoints(file.config);
    if (keys.length === 0)
        return false;
    const siteKeys = Object.keys(site);
    if (keys.some((k) => !siteKeys.includes(k)) || siteKeys.some((k) => !keys.includes(k)))
        return true;
    return scaledBreakpoints(file, proposeBreakpointMap(file.config, site), site).length > 0;
}
